import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Plus } from "lucide-react";
import { useCatalog } from "../catalog";
import { useLang } from "../i18n";
import { formatPrice } from "../currency";

const L = {
    en: { kicker: "The Boutique", title: "Shop all attars", search: "Search by name or note…", all: "All", empty: "Nothing matches your search.", soldOut: "Sold out", from: "from", view: "View", add: "Add" },
    fr: { kicker: "La Boutique", title: "Tous nos attars", search: "Rechercher par nom ou note…", all: "Tous", empty: "Aucun résultat.", soldOut: "Épuisé", from: "dès", view: "Voir", add: "Ajouter" },
    ar: { kicker: "المتجر", title: "تسوّق كل العطور", search: "ابحث بالاسم أو النوتة…", all: "الكل", empty: "لا توجد نتائج.", soldOut: "نفد", from: "من", view: "عرض", add: "أضف" },
    bn: { kicker: "বুটিক", title: "সব আতর দেখুন", search: "নাম বা নোট দিয়ে খুঁজুন…", all: "সব", empty: "কিছু পাওয়া যায়নি।", soldOut: "স্টক শেষ", from: "থেকে", view: "দেখুন", add: "যোগ করুন" },
};
const CATS = [["french", "nav.frenchSeries"], ["bakhoor", "nav.bakhoor"], ["arabian", "nav.arabian"]];

export const ProductCard = ({ p, i, onAdd, l, lang }) => {
    const price = p.sizes ? Math.min(...p.sizes.map((s) => s.inr)) : p.effectivePrice;
    const canQuickAdd = !p.sizes && p.in_stock;
    return (
        <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
            className="group"
            data-testid={`shop-card-${p.id}`}
        >
            <Link to={`/product/${p.slug}`} className="block relative overflow-hidden border border-gold/25 bg-charcoal aspect-[4/5]">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                {p.gallery?.[1] && (
                    <img src={p.gallery[1]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700" loading="lazy" />
                )}
                {!p.in_stock && <span className="absolute top-3 left-3 bg-ivory/90 text-charcoal text-[9px] tracking-[0.25em] uppercase px-3 py-1.5">{l.soldOut}</span>}
                {p.originalPrice && <span className="absolute top-3 right-3 bg-gold text-ivory text-[9px] tracking-[0.25em] uppercase px-3 py-1.5">Sale</span>}
                <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal/80 to-transparent px-4 pb-3 pt-10 text-[9px] tracking-[0.3em] uppercase text-goldlight">{p.series}</span>
            </Link>
            <div className="flex items-start justify-between gap-4 mt-4">
                <div>
                    <Link to={`/product/${p.slug}`} className="font-serif text-xl text-charcoal hover:text-golddark transition-colors" data-testid={`shop-name-${p.id}`}>{p.name}</Link>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-ash mt-1">{p.tag}</p>
                </div>
                <div className="text-right shrink-0">
                    <p className="font-serif text-lg text-charcoal" data-testid={`shop-price-${p.id}`}>{p.sizes ? `${l.from} ` : ""}{formatPrice(price, lang, p.sizes ? p.sizes[0].aed : p.aed)}</p>
                    {p.originalPrice && <p className="text-xs text-ash line-through">{formatPrice(p.originalPrice, lang)}</p>}
                </div>
            </div>
            <div className="flex gap-2 mt-3">
                <Link to={`/product/${p.slug}`} data-testid={`shop-view-${p.id}`} className="flex-1 text-center border border-charcoal/40 text-charcoal px-4 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-charcoal hover:text-goldlight transition-colors duration-300">{l.view}</Link>
                {canQuickAdd && (
                    <button data-testid={`shop-add-${p.id}`} onClick={() => onAdd({ ...p, price: p.effectivePrice, aed: p.aed ?? null })} className="flex items-center justify-center gap-1 bg-charcoal text-goldlight px-5 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-golddark hover:text-ivory transition-colors duration-300">
                        <Plus size={12} /> {l.add}
                    </button>
                )}
            </div>
        </motion.article>
    );
};

export default function ShopPage({ onAdd }) {
    const { products, loading } = useCatalog();
    const { lang, t } = useLang();
    const l = L[lang] ?? L.en;
    const [params, setParams] = useSearchParams();
    const cat = params.get("category") || "";
    const [q, setQ] = useState(params.get("q") || "");

    const list = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return products.filter((p) => (!cat || p.category === cat) && (!needle || [p.name, p.tag, p.description, ...Object.values(p.notes || {}).flat()].join(" ").toLowerCase().includes(needle)));
    }, [products, cat, q]);

    const setCat = (c) => setParams(c ? { category: c } : {});

    return (
        <div className="pt-28 sm:pt-36 pb-28 px-6 sm:px-10 bg-ivory min-h-screen" data-testid="shop-page">
            <div className="max-w-7xl mx-auto">
                <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-3">{l.kicker}</p>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-12">{l.title}</h1>

                <div className="flex flex-col md:flex-row md:items-center gap-5 mb-14">
                    <div className="flex flex-wrap gap-2" data-testid="shop-category-filters">
                        {[["", null], ...CATS].map(([c, key]) => (
                            <button key={c || "all"} data-testid={`shop-filter-${c || "all"}`} onClick={() => setCat(c)} className={`px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors duration-300 ${cat === c ? "bg-charcoal text-goldlight border-charcoal" : "border-gold/30 text-smoke hover:border-gold"}`}>
                                {key ? t(key) : l.all}
                            </button>
                        ))}
                    </div>
                    <label className="relative md:ml-auto md:w-80">
                        <Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-golddark" />
                        <input data-testid="shop-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={l.search} className="w-full bg-transparent border-b border-charcoal/20 focus:border-gold outline-none py-2 pl-6 text-sm text-charcoal transition-colors" />
                    </label>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-cream animate-pulse" />)}</div>
                ) : list.length === 0 ? (
                    <p className="font-serif text-2xl text-ash italic py-20 text-center" data-testid="shop-empty">{l.empty}</p>
                ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14" data-testid="shop-grid">
                        {list.map((p, i) => <ProductCard key={p.id} p={p} i={i} onAdd={onAdd} l={l} lang={lang} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
